import React, { useRef } from 'react';
import ReactDOM from 'react-dom/client';
import hasOwn from 'hasown';
import { AuthProvider } from 'react-oidc-context';
import { Provider as ReduxProvider } from 'react-redux';

import App from './App';
import MissingEnvVariablesError from './components/views/MissingEnvVariablesError';
import './index.css';
import { AuthHelper, AuthType } from './libs/auth/AuthHelper';
import { RootState, store } from './redux/app/store';
import { getMissingEnvVariables } from './checkEnv';
import * as tracking from './libs/tracking';
import { useFeatureFlags } from './components/FeatureFlagInitializer';
import { NoneAuthProvider } from './libs/auth/NoneAuthContext';
import { OidcAuthHelper } from './libs/auth/OidcAuthHelper';
import { useAuthSetup } from './libs/hooks/useAuthSetup';
import { useHostContextInitialization } from './libs/hooks/useHostContextInitialization';
import { useAppSelector } from './redux/app/hooks';
import { OidcAgentAuthProvider } from './livechat/agent/auth/OidcAgentAuthProvider';
import { isEntraConfigured } from './livechat/agent/auth/entraConfig';
import { useAgentEntraLoginFlag } from './livechat/agent/auth/useAgentEntraLoginFlag';

// Polyfill for Object.hasOwn
// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
if (!Object.hasOwn) {
    Object.hasOwn = function (obj, key) {
        return hasOwn(obj, key);
    };
}

tracking.init();

const AppWithThemeAndAuth: React.FC = () => {
    useFeatureFlags();

    return <App />;
};

// useAuthSetup() drives silent sign-in / redirect-to-login. Lift it out of
// AppWithThemeAndAuth so the agent console (which renders under a different branch)
// also gets it — otherwise visiting /agents/* while unauthenticated runs the agent UI
// with a null access token and every API call fails.
const AuthBootstrap: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    useAuthSetup();
    return <>{children}</>;
};

// LiveChat agent console — lazy-loaded so the customer bundle stays unchanged. Delete
// this import + the branch in Root() to remove the agent console. The admin console is
// mounted inside it at /agents/admin (so it's covered by the same SWA /agents/* route).
const AgentRootApp = React.lazy(() => import('./livechat/agent/AgentRootApp'));

// Entra (MSAL) auth provider for the agent console (#1994). Lazy so the @azure/msal-browser bundle
// is pulled only when the LiveChatAgentEntraLoginEnabled flag is on and Entra is configured —
// customers and flag-off agents never download it.
const EntraAgentAuthProvider = React.lazy(() =>
    import('./livechat/agent/auth/EntraAgentAuthProvider').then((m) => ({ default: m.EntraAgentAuthProvider })),
);

// `popstate` only fires for browser back/forward, not for react-router's `navigate()`
// (which uses `history.pushState` internally). To catch SPA-internal navigations across
// the customer ↔ /agents boundary, wrap pushState/replaceState once at module load and
// emit a custom event. Idempotent — guarded against double-wrapping under HMR or
// duplicate module instances.
const HISTORY_CHANGE_EVENT = 'livechat:historychange';
const HISTORY_PATCH_SENTINEL = '__livechatHistoryPatched';

interface PatchedHistory extends History {
    [HISTORY_PATCH_SENTINEL]?: true;
}

const patchHistoryOnce = (): void => {
    if (typeof window === 'undefined') return;
    const history = window.history as PatchedHistory;
    if (history[HISTORY_PATCH_SENTINEL]) return;
    history[HISTORY_PATCH_SENTINEL] = true;

    const fire = (): void => {
        window.dispatchEvent(new Event(HISTORY_CHANGE_EVENT));
    };
    const originalPush = history.pushState.bind(history);
    const originalReplace = history.replaceState.bind(history);
    history.pushState = function pushState(...args: Parameters<History['pushState']>): void {
        originalPush(...args);
        fire();
    };
    history.replaceState = function replaceState(...args: Parameters<History['replaceState']>): void {
        originalReplace(...args);
        fire();
    };
};

const subscribeToHistory = (callback: () => void): (() => void) => {
    patchHistoryOnce();
    window.addEventListener('popstate', callback);
    window.addEventListener(HISTORY_CHANGE_EVENT, callback);
    return () => {
        window.removeEventListener('popstate', callback);
        window.removeEventListener(HISTORY_CHANGE_EVENT, callback);
    };
};
const getCurrentPathname = (): string => (typeof window === 'undefined' ? '/' : window.location.pathname);
const getServerPathname = (): string => '/';

const ReduxInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    useHostContextInitialization();
    return <>{children}</>;
};

const OidcProviderWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [oidcConfig, setOidcConfig] = React.useState<ReturnType<typeof OidcAuthHelper.getOidcConfig> | null>(null);
    const hostContextInitialized = useAppSelector((state: RootState) => state.hostContext.isInitialized);
    const hasSetOidcConfig = useRef(false);

    React.useEffect(() => {
        if (hostContextInitialized && !oidcConfig && !hasSetOidcConfig.current) {
            const config = OidcAuthHelper.getOidcConfig(store);
            setOidcConfig(config);
            hasSetOidcConfig.current = true;
        }
    }, [hostContextInitialized, oidcConfig]);

    if (!oidcConfig) {
        return null;
    }

    return <AuthProvider {...oidcConfig}>{children}</AuthProvider>;
};

// Agent console (also hosts the admin console at /agents/admin). Picks its auth flow at runtime:
// Microsoft Entra ID (a separate MSAL session, #1994) when the LiveChatAgentEntraLoginEnabled flag
// is on and Entra is configured, otherwise the pre-#1994 behaviour of reusing the customer's Visma
// Online OIDC session. In Entra mode we deliberately do NOT mount OidcProviderWrapper/AuthBootstrap:
// their useAuthSetup would trigger the Visma Online silent sign-in / redirect that clobbers the
// agent's session — the exact bug #1994 fixes. Either way AgentRootApp sees a uniform AgentAuth seam.
const AgentEntry: React.FC<{ shouldUseNone: boolean }> = ({ shouldUseNone }) => {
    const entraEnabled = useAgentEntraLoginFlag();

    const agentApp = (
        <React.Suspense fallback={null}>
            <AgentRootApp />
        </React.Suspense>
    );

    if (!shouldUseNone && entraEnabled && isEntraConfigured()) {
        return (
            <React.Suspense fallback={null}>
                <EntraAgentAuthProvider>{agentApp}</EntraAgentAuthProvider>
            </React.Suspense>
        );
    }

    // Still resolving the flag (non-None mode): render nothing briefly rather than flashing the
    // Visma Online flow and then possibly switching to Entra once /info resolves.
    if (!shouldUseNone && entraEnabled === undefined) {
        return null;
    }

    // Flag off (or local None mode): reuse the customer's Visma Online OIDC session, surfaced
    // through the OidcAgentAuthProvider seam.
    const oidcAgentApp = <OidcAgentAuthProvider>{agentApp}</OidcAgentAuthProvider>;
    return shouldUseNone ? (
        <NoneAuthProvider>
            <AuthBootstrap>{oidcAgentApp}</AuthBootstrap>
        </NoneAuthProvider>
    ) : (
        <OidcProviderWrapper>
            <AuthBootstrap>{oidcAgentApp}</AuthBootstrap>
        </OidcProviderWrapper>
    );
};

const Root: React.FC = () => {
    const missingEnvVariables = getMissingEnvVariables();
    const validEnvFile = missingEnvVariables.length === 0;
    const shouldUseNone = validEnvFile && AuthHelper.getAuthType() === AuthType.None;

    // Subscribe to popstate so the agent/customer branch re-evaluates on browser
    // back/forward. Without this, navigating back from /agents/queue to / would keep
    // <AgentRootApp /> mounted under a non-/agents URL (and vice versa).
    const pathname = React.useSyncExternalStore(subscribeToHistory, getCurrentPathname, getServerPathname);
    const isAgent = pathname.startsWith('/agents');

    if (!validEnvFile) {
        return <MissingEnvVariablesError missingVariables={missingEnvVariables} />;
    }

    // Agent console branch — its auth flow is chosen inside AgentEntry, which (unlike the customer
    // branch) must be free to skip the Visma Online AuthProvider entirely in Entra mode.
    if (isAgent) {
        return (
            <ReduxProvider store={store}>
                <ReduxInitializer>
                    <AgentEntry shouldUseNone={shouldUseNone} />
                </ReduxInitializer>
            </ReduxProvider>
        );
    }

    // Customer chat shell — unchanged: shared Redux store, ReduxInitializer, and the Visma Online
    // (or None) OIDC session.
    return (
        <ReduxProvider store={store}>
            <ReduxInitializer>
                {shouldUseNone ? (
                    <NoneAuthProvider>
                        <AuthBootstrap>
                            <AppWithThemeAndAuth />
                        </AuthBootstrap>
                    </NoneAuthProvider>
                ) : (
                    <OidcProviderWrapper>
                        <AuthBootstrap>
                            <AppWithThemeAndAuth />
                        </AuthBootstrap>
                    </OidcProviderWrapper>
                )}
            </ReduxInitializer>
        </ReduxProvider>
    );
};

let container: HTMLElement | null = null;
document.addEventListener('DOMContentLoaded', () => {
    if (!container) {
        container = document.getElementById('root');
        if (!container) {
            throw new Error('Could not find root element');
        }
        const root = ReactDOM.createRoot(container);
        root.render(
            <React.StrictMode>
                <Root />
            </React.StrictMode>,
        );
    }
});
